import Link from 'next/link';
import styles from '../pageScope.module.css';
import { useReducedMotion } from './Effects';
import { MarketingFooter, MarketingHeader } from './MarketingChrome';
import {
  createElement,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { ConversionScreen } from './PricingEvaluationOverlay';
import { signaturePricingEnabled } from '~/lib/marketing/signature-pricing';
import { marketingContent } from './marketing-page-content';
import { marketingPageKeys } from './marketing-page-types';
import type {
  ContentNode,
  MarketingPageContent,
  MarketingPageKey,
} from './marketing-page-types';

const PricingEvaluationOverlay = lazy(() =>
  import('./PricingEvaluationOverlay').then((module) => ({
    default: module.PricingEvaluationOverlay,
  })),
);
const SignaturePricingOverlay = lazy(() =>
  import('./SignaturePricingOverlay').then((module) => ({
    default: module.SignaturePricingOverlay,
  })),
);
function RichContent({ nodes }: { nodes: ContentNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        if (typeof node === 'string') return node;
        const children = <RichContent nodes={node.children} />;
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex -- This named scroll region needs keyboard focus so users can scroll wide policy tables. */
        if (node.tag === 'table')
          return (
            <div
              key={index}
              className="marketing-table-scroll"
              role="region"
              aria-label="Third-party cookie services"
              tabIndex={0}
            >
              <table>{children}</table>
            </div>
          );
        /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
        if (node.tag === 'a') {
          const external = node.href?.startsWith('https://');
          return (
            <Link
              prefetch={false}
              key={index}
              href={node.href ?? '/'}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
            >
              {children}
            </Link>
          );
        }
        if (node.tag === 'br') return <br key={index} />;
        return createElement(
          node.tag,
          { key: index, ...(node.tag === 'th' ? { scope: 'col' } : {}) },
          children,
        );
      })}
    </>
  );
}

export function MarketingPageShell({
  page,
  title,
  lede,
  effectiveDate,
  children,
  onStart,
  onPricing,
  menu,
  onMenu,
  menuButton,
}: {
  page: MarketingPageKey | undefined;
  title: string;
  lede?: string;
  effectiveDate?: string;
  children: ReactNode;
  onStart: () => void;
  onPricing: () => void;
  menu: boolean;
  onMenu: () => void;
  menuButton: React.RefObject<HTMLButtonElement>;
}) {
  const legal = page === 'terms' || page === 'privacy' || page === 'cookies';
  const reduced = useReducedMotion();
  return (
    <div className="marketing-site">
      <Link prefetch={false} className="marketing-skip" href="#page-content">
        Skip to content
      </Link>
      <MarketingHeader
        home={false}
        menu={menu}
        menuButton={menuButton}
        onMenu={onMenu}
        onNav={() => {
          if (menu) onMenu();
        }}
        onLogin={() => window.location.assign('/auth/sign-in')}
        onPricing={onPricing}
        onEvaluation={onStart}
        flareEnabled={!reduced}
        paused={false}
      />
      <main
        id="page-content"
        className={`marketing-main marketing-wrap marketing-page--${page ?? 'not-found'}`}
        tabIndex={-1}
      >
        <div
          className={`marketing-page-heading${legal ? ' marketing-page-heading--legal' : ''}`}
        >
          {legal && (
            <p className="marketing-legal-company">DirectorDeck · Legal</p>
          )}
          {!legal && effectiveDate && (
            <p className="marketing-effective-date">{effectiveDate}</p>
          )}
          <h1>{title}</h1>
          {legal && effectiveDate && (
            <p className="marketing-effective-date">{effectiveDate}</p>
          )}
          {lede && <p className="marketing-lede">{lede}</p>}
        </div>
        {children}
      </main>
      <MarketingFooter />
    </div>
  );
}

function FaqContent({
  questions,
}: {
  questions: NonNullable<MarketingPageContent['questions']>;
}) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  return (
    <div className="marketing-faq">
      {questions.map((item, index) => {
        const open = expanded.has(index);
        return (
          <section className="marketing-faq-item" key={item.question}>
            <h2>
              <button
                id={`question-${index}`}
                data-test="faq-item"
                aria-expanded={open}
                aria-controls={`answer-${index}`}
                onClick={() =>
                  setExpanded((current) => {
                    const next = new Set(current);
                    if (next.has(index)) next.delete(index);
                    else next.add(index);
                    return next;
                  })
                }
              >
                <span>{item.question}</span>
                <span className="marketing-disclosure-icon" aria-hidden="true">
                  {open ? '−' : '+'}
                </span>
              </button>
            </h2>
            <div
              id={`answer-${index}`}
              data-test="faq-answer"
              role="region"
              aria-labelledby={`question-${index}`}
              hidden={!open}
            >
              <p>{item.answer}</p>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function PageBody({
  page,
  content,
  onStart,
}: {
  page: MarketingPageKey;
  content: MarketingPageContent;
  onStart: () => void;
}) {
  const legal = ['terms', 'privacy', 'cookies'].includes(page);
  if (legal)
    return (
      <article className="marketing-legal-article marketing-prose">
        <div className="marketing-legal-intro">
          <RichContent nodes={content.intro} />
        </div>
        {content.sections.map((section, index) => (
          <section
            key={section.title}
            id={`section-${index + 1}`}
            aria-labelledby={`heading-${index + 1}`}
          >
            <h2 id={`heading-${index + 1}`}>{section.title}</h2>
            <RichContent nodes={section.body} />
          </section>
        ))}
      </article>
    );
  if (content.questions) return <FaqContent questions={content.questions} />;
  return (
    <>
      {content.intro.length > 0 && (
        <div className="marketing-intro marketing-prose">
          <RichContent nodes={content.intro} />
        </div>
      )}
      <div
        className={
          page === 'about'
            ? 'marketing-editorial'
            : `marketing-cards marketing-cards--${page}`
        }
      >
        {content.sections.map((section, index) => (
          <section
            key={section.title}
            className="marketing-content-section"
            aria-labelledby={`heading-${index + 1}`}
          >
            <h2 id={`heading-${index + 1}`}>{section.title}</h2>
            <div className="marketing-prose">
              <RichContent nodes={section.body} />
            </div>
          </section>
        ))}
      </div>
      {content.cta && (
        <section className="marketing-closing">
          <img src="/landing/assets/mark.svg" alt="" width="40" height="48" />
          <h2>{content.cta.title}</h2>
          <button className="marketing-button" onClick={onStart}>
            {content.cta.label}
            <span aria-hidden="true">↗</span>
          </button>
        </section>
      )}
    </>
  );
}

export default function MarketingPages({ pathname }: { pathname: string }) {
  const key = pathname.slice(1);
  const page = marketingPageKeys.find((route) => route === key);
  const content = page ? marketingContent[page] : undefined;
  const [menu, setMenu] = useState(false);
  const [conversionScreen, setConversionScreen] =
    useState<ConversionScreen>('home');
  const menuButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const start = () => {
    opener.current = menu
      ? menuButton.current
      : (document.activeElement as HTMLElement);
    setMenu(false);
    setConversionScreen('evaluation');
  };
  const pricing = () => {
    opener.current = menu
      ? menuButton.current
      : (document.activeElement as HTMLElement);
    setMenu(false);
    setConversionScreen('pricing');
  };

  useEffect(() => {
    if (!menu) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenu(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [menu]);

  return (
    <div className={styles.pageScope}>
      <MarketingPageShell
        page={page}
        title={content?.title ?? 'Page not found'}
        lede={content?.lede}
        effectiveDate={content?.effectiveDate}
        onStart={start}
        onPricing={pricing}
        menu={menu}
        onMenu={() => setMenu(!menu)}
        menuButton={menuButton}
      >
        {page && content ? (
          <PageBody page={page} content={content} onStart={start} />
        ) : (
          <div className="marketing-not-found">
            <p>This page could not be found.</p>
            <Link prefetch={false} className="marketing-button" href="/">
              Home<span aria-hidden="true">↗</span>
            </Link>
          </div>
        )}
      </MarketingPageShell>
      {conversionScreen !== 'home' && (
        <Suspense fallback={null}>
          {signaturePricingEnabled() && conversionScreen === 'pricing' ? (
            <SignaturePricingOverlay
              onClose={() => setConversionScreen('home')}
              onEvaluate={() => setConversionScreen('evaluation')}
              onContact={() => window.location.assign('/contact')}
              returnFocusTo={opener.current}
            />
          ) : (
            <PricingEvaluationOverlay
              screen={conversionScreen}
              onNavigate={setConversionScreen}
              onClose={() => setConversionScreen('home')}
              returnFocusTo={opener.current}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}
